import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Search, Loader2, CheckCircle, AlertCircle, X } from "lucide-react";

interface AddListingBarProps {
  onAdd: (url: string) => Promise<void>;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  onClearMessages: () => void;
}

export function AddListingBar({ onAdd, loading, error, successMessage, onClearMessages }: AddListingBarProps) {
  const [url, setUrl] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!url.trim() || loading) return;
    await onAdd(url.trim());
    setUrl("");
  };

  return (
    <div className="py-8">
      <form onSubmit={handleSubmit} className="relative">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 group">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors">
              <Search className="w-4 h-4" />
            </div>
            <input
              type="url"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                onClearMessages();
              }}
              placeholder="Klistra in en Hemnet-länk..."
              className="w-full h-12 pl-11 pr-4 rounded-xl bg-secondary/50 border border-border/50 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all duration-200"
              disabled={loading}
            />
          </div>
          <Button
            type="submit"
            disabled={!url.trim() || loading}
            className="h-12 px-6 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-medium tracking-wide text-sm transition-all duration-200 disabled:opacity-40"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="ml-2">Sparar...</span>
              </>
            ) : (
              "Spara bostad"
            )}
          </Button>
        </div>
      </form>

      {/* Status messages */}
      {(error || successMessage) && (
        <div
          className={`mt-3 flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm animate-in fade-in slide-in-from-top-2 duration-200 ${
            error
              ? "bg-destructive/10 text-destructive border border-destructive/20"
              : "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
          }`}
        >
          {error ? <AlertCircle className="w-4 h-4 shrink-0" /> : <CheckCircle className="w-4 h-4 shrink-0" />}
          <span className="flex-1">{error ?? successMessage}</span>
          <button
            onClick={onClearMessages}
            className="p-0.5 rounded hover:bg-white/10 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
